import defaultUser from '@assets/img/jpg/avatar.jpg'
import Button from '@components/buttons/button/button.component'
import Selector from '@components/inputs/selector/selector.component'
import Loader from '@components/loader/loader.component'
import localForageHelper from '@helpers/localforage.helper'
import { SocialNetworksAvailables } from '@interfaces/socialnetworks.interface'
import { FacebookDiscoveryUser, Page } from '@interfaces/user.interface'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ColorIconHelper } from '@/hub-shared'
import './social-networks-content.component.scoped.scss'

interface Props {
  socialNetwork: SocialNetworksAvailables
  pages: Page[]
  selectedProfile: string
  setSelectedProfile: (profile: string) => void
  loading: boolean
  stepActive: number
  width: number
  height: number
}

export default ({ socialNetwork, pages, selectedProfile, setSelectedProfile, loading, stepActive, width, height }: Props) => {
  const colorIconHelper = useMemo(() => new ColorIconHelper(), [])
  const mappedPages = useMemo(
    () => (['instagram', 'facebook'].includes(socialNetwork) ? pages?.map((page) => page?.instagram) : pages),
    [socialNetwork, pages]
  )
  const [buttonDisabled, setButtonDisabled] = useState(!selectedProfile)

  const { t } = useTranslation('modalVinculateSocialNetwork')

  useEffect(() => {
    const checkProfileSelected = async () => {
      if (mappedPages?.length === 1 && buttonDisabled && selectedProfile !== mappedPages?.[0]?.user_id) {
        setSelectedProfile(mappedPages?.[0]?.user_id)
      } else {
        const businessProfile = (await localForageHelper.getItem('businessProfileSelected')) as FacebookDiscoveryUser
        if (businessProfile) {
          setSelectedProfile(businessProfile?.id)
          setButtonDisabled(false)
        }
      }
    }

    checkProfileSelected()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSelectProfileToSwitch = useCallback(
    (page) => {
      if (page?.user_id === selectedProfile) {
        setSelectedProfile(null)
        setButtonDisabled(true)
        return
      }

      setSelectedProfile(page?.user_id)
      setButtonDisabled(false)
    },
    [selectedProfile, setSelectedProfile]
  )

  if (loading) {
    return <Loader size="big" className="m-2" />
  }

  return (
    <div
      className="pages-list"
      style={{
        height: width < 991 ? `calc(${height}px - 62px - 9px - 45px)` : null,
      }}
    >
      {mappedPages?.length && stepActive === 0 ? (
        <span
          className="text-left text-bold py-3"
          style={{ width: '100%' }}
          dangerouslySetInnerHTML={{ __html: t('selectProfileFrom', { network: socialNetwork }) }}
        />
      ) : null}
      {!loading &&
        stepActive === 0 &&
        mappedPages?.map((page, index) => (
          <div key={index} className="page-item-profile" style={{ borderTop: index === 0 ? '1px solid var(--border_color)' : null }}>
            <Button
              id={`social-network-item-profile-${index + 1}`}
              className="p-0"
              color="transparent"
              borderColor="transparent"
              onLoad={() => (mappedPages.length === 1 && selectedProfile === page?.user_id ? () => onSelectProfileToSwitch(page) : null)}
              style={{ width: 'calc(100% + 2rem)', left: '-1rem', gap: '1rem' }}
            >
              <label htmlFor={`select-profile-${socialNetwork}-${page?.user_id}`} className="page-item">
                <div className="page-item-image-container">
                  <figure>
                    <img
                      alt="page-item"
                      className="page-item-image"
                      src={page?.picture || defaultUser}
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null
                        currentTarget.src = defaultUser
                      }}
                    />
                  </figure>
                  <span
                    className={`social-icon background-${colorIconHelper.setColor(
                      socialNetwork === 'facebook' ? 'instagram' : socialNetwork
                    )}`}
                  >
                    <i
                      className={`fa-brands fa-${colorIconHelper.setIcon(
                        socialNetwork === 'facebook' ? 'instagram' : socialNetwork
                      )} small`}
                      style={{ color: 'white' }}
                    ></i>
                  </span>
                </div>

                <div className="page-item-texts">
                  <div className={`page-item-username ${page?.user_id === selectedProfile ? 'active' : ''}`}>
                    {socialNetwork !== 'youtube' && '@'}
                    {page?.username}
                  </div>
                  <Selector
                    name={`select-profile-${socialNetwork}-${page?.user_id}`}
                    id={`select-profile-${socialNetwork}-${page?.user_id}`}
                    errorSpan={false}
                    className="selector-profile"
                    value={page}
                    checked={page?.user_id === selectedProfile}
                    onChange={(checked: boolean) => {
                      if (checked) {
                        setSelectedProfile(page?.user_id)
                      }
                    }}
                    type="radio"
                  />
                </div>
              </label>
            </Button>
          </div>
        ))}
    </div>
  )
}
